import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Mutex, MutexInterface } from "async-mutex";
import { first, isNull, isUndefined, round, sum } from "lodash-es";
import {
  defer,
  forkJoin,
  from,
  iif,
  lastValueFrom,
  Observable,
  of,
  throwError
} from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { LetterheadBackend } from "src/app/backend/api/letterhead/letterhead.backend";
import { MedicalServiceBackend } from "src/app/backend/api/medical-service/medical-service.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { QuoteBackend } from "src/app/backend/api/quote.backend";
import { RateTableBackend } from "src/app/backend/api/rate-table/rate-table.backend";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import {
  MedicalServiceId,
  MedicalServiceLight
} from "src/app/backend/models/medical-service";
import { PetLight } from "src/app/backend/models/pet/pet";
import {
  QuoteItem,
  QuoteItemType
} from "src/app/backend/models/quote/quote.item";
import { RateTableLight } from "src/app/backend/models/rate-table/rate-table";
import { WorkOrder } from "src/app/backend/models/work-order/work-order";
import { MODULES } from "src/app/core/modules";
import { appState } from "src/app/core/state";
import { ModuleService } from "src/app/services/feature-access-control/module.service";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { PrintService } from "src/app/services/print/print.service";
import { TemplateService } from "src/app/services/template.service";
import { blobToDataUrl } from "src/app/support/blob";
import { formatCurrency } from "src/app/support/currency";
import { getCustomerNominative } from "src/app/support/customer";
import { dateUtil } from "src/app/support/date-util";
import { UserNotify } from "src/app/support/notify-decorator";
import { omitUndefinedAndNullProperties } from "src/app/support/object";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { sntTranslate } from "src/app/support/translate";
import { environment } from "src/environments/environment";
import { Book, OpenBooks } from "../models/book/book";
import {
  BookItem,
  BookItemCreate,
  BookItemType,
  BookItemUnit,
  bookItemUnitFromQuoteItemUnit,
  quoteItemTypeToBookItemType
} from "../models/book/book-item";
import { WorkOrderBackend } from "src/app/backend/api/work-order/work-order.backend";
import {
  DrugPerscriptionType,
  DrugPrescription
} from "src/app/backend/models/prescription/drug-prescription";
import { PrescriptionBackend } from "src/app/backend/api/prescription.backend";
import { DrugBackend } from "src/app/backend/api/drug.backend";
import { DrugLight } from "src/app/backend/models/drug";
import { ProductBackend } from "src/app/backend/api/product.backend";
import { InvoiceType } from "src/app/backend/models/invoice/invoice";

@Injectable()
export class BookBackend {
  private readonly loadOrCreateMutex = new Mutex();

  private readonly http = inject(HttpClient);
  private readonly templateService = inject(TemplateService);
  private readonly printService = inject(PrintService);
  private readonly quoteBackend = inject(QuoteBackend);
  private readonly petBackend = inject(PetBackend);
  private readonly storageBackend = inject(StorageBackend);
  private readonly medicalServiceBackend = inject(MedicalServiceBackend);
  private readonly rateTableBackend = inject(RateTableBackend);
  private readonly letterheadBackend = inject(LetterheadBackend);
  private readonly regionService = inject(RegionService);
  private readonly moduleService = inject(ModuleService);
  private readonly workOrderBackend = inject(WorkOrderBackend);
  private readonly prescriptionBackend = inject(PrescriptionBackend);
  private readonly drugBackend = inject(DrugBackend);
  private readonly productBackend = inject(ProductBackend);

  load(patientId: string): Observable<Book[]> {
    return this.http
      .get<Book[]>(
        `${environment.snoots.api.billing}/patients/${patientId}/books`
      )
      .pipe(
        map((books: Book[]) => {
          return books.map((book: Book) => extendBook(book));
        })
      );
  }

  loadOrCreate({ patientId }: { patientId: string }): Observable<Book> {
    return from(this.loadOrCreateMutex.acquire()).pipe(
      switchMap((mutexRealeaser: MutexInterface.Releaser) => {
        return this.load(patientId).pipe(
          switchMap((books) =>
            iif(
              () => !books || books.length === 0,
              defer(() => this.create(patientId)),
              defer(() => of(first(books)!))
            ).pipe(
              tap(() => {
                mutexRealeaser();
              })
            )
          ),
          catchError((error) => {
            mutexRealeaser();
            return throwError(() => error);
          })
        );
      })
    );
  }

  loadSingle(patientId: string): Observable<Book | undefined> {
    return this.http
      .get<Book[]>(
        `${environment.snoots.api.billing}/patients/${patientId}/books`
      )
      .pipe(
        map((books) => first(books)),
        map((book: Book | undefined) => (book ? sortBookItems(book) : book)),
        map((book: Book | undefined) => (book ? extendBook(book) : book))
      );
  }

  loadSingleByTicket({
    patientId,
    ticketId
  }: {
    patientId: string;
    ticketId: string;
  }): Observable<Book | undefined> {
    return this.loadSingle(patientId).pipe(
      map((book) => {
        if (!book) {
          return undefined;
        }

        return {
          ...book,
          items: book.items.filter((x) => x.ticket && x.ticket.id === ticketId)
        };
      })
    );
  }

  loadItemByPatient(
    patientiId: string,
    itemId: string
  ): Observable<BookItem | undefined> {
    return this.loadSingle(patientiId).pipe(
      map((book) => book?.items.find((x) => x.id === itemId))
    );
  }

  loadAvailableAdvancePaymentsByPatients(
    patientIds: string[]
  ): Observable<BookItem[]> {
    if (patientIds.length === 0) {
      return of([]);
    }

    return forkJoin(
      patientIds.map((patientId) => this.loadSingle(patientId))
    ).pipe(
      map((books) => {
        return (
          books
            .map((book) =>
              (book?.items ?? []).filter(
                (x) =>
                  x.type === BookItemType.ADVANCE_PAYMENT &&
                  (x.remainingAmount || 0) > 0 &&
                  !!x.invoice &&
                  x.invoice?.type === InvoiceType.ADVANCE_INVOICE
              )
            )
            .flat() ?? []
        );
      })
    );
  }

  loadOpenBooks(request: PageRequest): Observable<PaginatedResult<OpenBooks>> {
    return this.http
      .get<PaginatedResult<OpenBooks>>(
        `${environment.snoots.api.billing}/open-books`,
        {
          params: omitUndefinedAndNullProperties({
            p: request.page,
            s: request.pageSize
          })
        }
      )
      .pipe(
        switchMap((results) => {
          return iif(
            () => results?.docs.length > 0,
            defer(() => {
              return forkJoin(
                results.docs.map((doc) => {
                  if (doc.id) {
                    return this.petBackend.loadLight(doc.id).pipe(
                      map((patientLight) => {
                        return {
                          ...doc,
                          patient: patientLight
                        };
                      })
                    );
                  }

                  return of(doc);
                })
              );
            }).pipe(
              map((docs) => {
                return {
                  ...results,
                  docs
                };
              })
            ),
            of(results)
          );
        })
      );
  }

  @UserNotify({
    successMessage: marker("Conti aperti esportati"),
    errorMessage: marker("Impossibile esportare i conti aperti")
  })
  exportOpenBooks(type: string): Observable<any> {
    return this.http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/open-books/exports`,
      {
        type: type
      }
    );
  }

  @UserNotify({
    successMessage: (patientId: string, bookItem: Partial<BookItem>) =>
      sntTranslate(marker("name aggiunto al conto"), {
        name: bookItem.name || ""
      }),
    errorMessage: (patientId: string, bookItem: Partial<BookItem>) =>
      sntTranslate(marker("Impossibile aggiungere name al conto"), {
        name: bookItem.name
      })
  })
  add(
    patientId: string,
    request: Partial<BookItemCreate>
  ): Observable<BookItem> {
    return this.loadOrCreate({ patientId: patientId }).pipe(
      switchMap((book: Book) => {
        return this.http.post<BookItem>(
          `${environment.snoots.api.billing}/books/${book.id}/items`,
          request
        );
      })
    );
  }

  addWorkOrderWithPriceAndQuantitySetted({
    patientId,
    workOrder,
    medicalServices,
    rateTable,
    pricesAndQuantities
  }: {
    patientId: string;
    workOrder: WorkOrder;
    medicalServices: MedicalServiceLight[];
    rateTable: RateTableLight;
    pricesAndQuantities: Record<
      MedicalServiceId,
      { price: number; quantity: number; workOrderItemId: string }
    >;
  }): Observable<void | BookItem[]> {
    return iif(
      () => medicalServices.length > 0,
      defer(() => {
        return this.loadOrCreate({ patientId }).pipe(
          switchMap((book: Book) =>
            forkJoin(
              medicalServices.map(
                (medicalService: MedicalServiceLight, index: number) => {
                  const physiciansPercentage =
                    this.calculatePhysicianPercentage({
                      bookItemType: BookItemType.MEDICAL_SERVICE,
                      rateTable,
                      medicalService
                    });

                  const payload: BookItemCreate = {
                    type: BookItemType.MEDICAL_SERVICE,
                    physiciansPercentage,
                    number: medicalService.number,
                    hasEnpav: medicalService.hasEnpav,
                    name: medicalService.name,
                    description: medicalService.description,
                    quantity: pricesAndQuantities[medicalService.id].quantity,
                    unitPrice: pricesAndQuantities[medicalService.id].price,
                    origin: {
                      id: workOrder.id,
                      number: workOrder.number,
                      type: "work-order"
                    },
                    vat: medicalService.vat,
                    rateTable: {
                      id: rateTable.id,
                      name: rateTable.name
                    },
                    rateTableItemId: medicalService.id,
                    executedBy: [
                      {
                        id: appState.member!.id,
                        name: getCustomerNominative(appState.member),
                        percentage: 1
                      }
                    ],
                    executedAt: dateUtil().toISOString()
                  };

                  return this.workOrderBackend
                    .reconcileItemWithMedicalService({
                      id: workOrder.id,
                      itemId:
                        pricesAndQuantities[medicalService.id].workOrderItemId,
                      medicalServiceId: medicalService.id
                    })
                    .pipe(
                      switchMap(() => {
                        return this.http.post<BookItem>(
                          `${environment.snoots.api.billing}/books/${book.id}/items`,
                          payload
                        );
                      })
                    );
                }
              )
            )
          )
        );
      }),
      of(void 0)
    );
  }

  addQuoteWithPriceAndQuantityConfigured(
    quoteId: string,
    quoteNumber: string,
    patientId: string,
    minMax: boolean,
    quoteItems: QuoteItem[],
    pricesAndQuantityConfig: Record<
      string,
      {
        price: number;
        quantity: number;
        discount?: number;
        executedBy?: Array<{ id: string; name: string; percentage: number }>;
      }
    >
  ): Observable<string> {
    return this.loadOrCreate({ patientId: patientId }).pipe(
      switchMap((book: Book) => {
        return iif(
          () => quoteItems.length > 0,
          defer(() => {
            return forkJoin(
              quoteItems.map((item: QuoteItem) => {
                if (!!pricesAndQuantityConfig[item.id!]) {
                  return forkJoin({
                    defaultRateTable: this.rateTableBackend.loadDefault(),
                    medicalService: iif(
                      () => item.type === QuoteItemType.MEDICAL_SERVICE,
                      defer(() =>
                        this.medicalServiceBackend.loadLight(
                          item.rateTableItemId
                        )
                      ),
                      of(undefined)
                    )
                  }).pipe(
                    switchMap(
                      ({
                        defaultRateTable,
                        medicalService
                      }: {
                        defaultRateTable: RateTableLight | undefined;
                        medicalService: MedicalServiceLight | undefined;
                      }) => {
                        const physiciansPercentage =
                          this.calculatePhysicianPercentage({
                            bookItemType: quoteItemTypeToBookItemType(
                              item.type
                            ),
                            rateTable: defaultRateTable!,
                            medicalService
                          });

                        const payload: BookItemCreate = {
                          unit: bookItemUnitFromQuoteItemUnit(item.unit),
                          physiciansPercentage,
                          discount: item.discount,
                          number: item.number,
                          hasEnpav: item.hasEnpav,
                          type: quoteItemTypeToBookItemType(item.type),
                          name: item.name,
                          description: item.description,
                          vatNature: item.vatNature,
                          quantity: minMax
                            ? pricesAndQuantityConfig[item.id!].quantity
                            : item.quantity,
                          unitPrice: minMax
                            ? pricesAndQuantityConfig[item.id!].price
                            : item.unitPrice,
                          origin: {
                            id: quoteId,
                            number: quoteNumber,
                            type: "quote"
                          },
                          rateTableItemId: item.rateTableItemId,
                          vat: item.vat,
                          rateTable: item.rateTable
                            ? {
                                id: item.rateTable.id,
                                name: item.rateTable.name
                              }
                            : undefined,
                          rateable: item.rateable,
                          vatTaxAmount: item.vatTaxAmount,
                          taxedTotal: item.taxedTotal,
                          adjustment: item.adjustment,
                          adjustedTotal: item.adjustedTotal,
                          executedBy: pricesAndQuantityConfig[item.id!]
                            .executedBy || [
                            {
                              id: appState.member!.id,
                              name: getCustomerNominative(appState.member),
                              percentage: 1
                            }
                          ],
                          executedAt: dateUtil().toISOString()
                        };

                        return this.http
                          .post<BookItem>(
                            `${environment.snoots.api.billing}/books/${book.id}/items`,
                            payload
                          )
                          .pipe(
                            switchMap((bookItem: BookItem) => {
                              return this.quoteBackend
                                .updateItemSilently(quoteId, item.id!, {
                                  bookItemIds: [
                                    ...(item.bookItemIds || []),
                                    bookItem.id
                                  ]
                                })
                                .pipe(map(() => bookItem));
                            })
                          );
                      }
                    )
                  );
                }

                return of(item.id);
              })
            );
          }).pipe(map(() => book.id)),
          of(book.id)
        );
      })
    );
  }

  addPrescriptionWithPriceAndQuantityConfigured(
    prescriptionId: string,
    prescriptionNumber: string,
    patientId: string,
    minMax: boolean,
    prescriptionItems: DrugPrescription[],
    pricesAndQuantityConfig: Record<
      string,
      {
        price: number;
        quantity: number;
        discount?: number;
        vat?: number;
        executedBy?: Array<{ id: string; name: string; percentage: number }>;
      }
    >
  ): Observable<string> {
    return this.loadOrCreate({ patientId: patientId }).pipe(
      switchMap((book: Book) => {
        return iif(
          () => prescriptionItems.length > 0,
          defer(() => {
            return forkJoin(
              prescriptionItems.map((item: DrugPrescription) => {
                const itemId =
                  item.type === DrugPerscriptionType.MEDICINE
                    ? item.drugId
                    : item.id;

                const itemConfig = pricesAndQuantityConfig[itemId!];

                if (!!itemConfig) {
                  return forkJoin({
                    defaultRateTable: this.rateTableBackend.loadDefault(),
                    drugLight: iif(
                      () => !!item.drugId,
                      defer(() => this.drugBackend.loadLight(item.drugId!)),
                      of(undefined)
                    ),
                    medicalId: iif(
                      () => !!item.drugId,
                      defer(() => this.drugBackend.loadLight(item.drugId!)),
                      of(undefined)
                    )
                  }).pipe(
                    switchMap(
                      ({
                        defaultRateTable,
                        drugLight
                      }: {
                        defaultRateTable: RateTableLight | undefined;
                        drugLight: DrugLight | undefined;
                      }) => {
                        return forkJoin({
                          defaultRateTable: of(defaultRateTable),
                          drugLight: of(drugLight),
                          productId: iif(
                            () => !!drugLight,
                            of(undefined),
                            defer(() =>
                              this.productBackend
                                .create({
                                  name: item.name!,
                                  unitPrice: itemConfig.price,
                                  vat: itemConfig.vat!
                                })
                                .pipe(map(({ id }) => id))
                            )
                          )
                        }).pipe(
                          switchMap(
                            ({
                              defaultRateTable,
                              drugLight,
                              productId
                            }: {
                              defaultRateTable: RateTableLight | undefined;
                              drugLight: DrugLight | undefined;
                              productId: string | undefined;
                            }) => {
                              const physiciansPercentage =
                                this.calculatePhysicianPercentage({
                                  bookItemType: BookItemType.DRUG_ASSIGNMENT,
                                  rateTable: defaultRateTable!,
                                  medicalService: undefined
                                });

                              const payload: BookItemCreate = {
                                unit: BookItemUnit.PACKAGE,
                                physiciansPercentage,
                                discount: itemConfig.discount,
                                hasEnpav: undefined,
                                number: drugLight?.number!,
                                type: !!productId
                                  ? BookItemType.PRODUCT_SALE
                                  : BookItemType.DRUG_ASSIGNMENT,
                                name: item.name!,
                                description: item.description,
                                vatNature: undefined,
                                quantity: itemConfig.quantity,
                                unitPrice: itemConfig.price,
                                origin: {
                                  id: prescriptionId,
                                  number: prescriptionNumber,
                                  type: "prescription"
                                },
                                rateTableItemId: drugLight?.id ?? productId!,
                                vat: itemConfig.vat!,
                                rateTable: {
                                  id: defaultRateTable?.id!,
                                  name: defaultRateTable?.name!
                                },
                                rateable: drugLight?.rateable,
                                executedBy: itemConfig.executedBy || [
                                  {
                                    id: appState.member!.id,
                                    name: getCustomerNominative(
                                      appState.member
                                    ),
                                    percentage: 1
                                  }
                                ],
                                executedAt: dateUtil().toISOString()
                              };
                              return this.http
                                .post<BookItem>(
                                  `${environment.snoots.api.billing}/books/${book.id}/items`,
                                  payload
                                )
                                .pipe(
                                  switchMap((bookItem: BookItem) => {
                                    return this.prescriptionBackend
                                      .updateDrug(prescriptionId, item.id, {
                                        bookItemId: bookItem.id
                                      })
                                      .pipe(map(() => bookItem));
                                  })
                                );
                            }
                          )
                        );
                      }
                    )
                  );
                }

                return of(item.id);
              })
            );
          }).pipe(map(() => book.id)),
          of(book.id)
        );
      })
    );
  }

  private calculatePhysicianPercentage({
    bookItemType,
    rateTable,
    medicalService
  }: {
    bookItemType: BookItemType;
    rateTable: RateTableLight;
    medicalService: MedicalServiceLight | undefined;
  }): number {
    switch (bookItemType) {
      case BookItemType.MEDICAL_SERVICE: {
        return isUndefined(medicalService?.physiciansPercentage) ||
          isNull(medicalService?.physiciansPercentage)
          ? rateTable.serviceModifier?.physiciansPercentage || 0
          : medicalService?.physiciansPercentage;
      }
      case BookItemType.DRUG_ASSIGNMENT: {
        return rateTable.drugModifier?.physiciansPercentage || 0;
      }
      case BookItemType.PRODUCT_SALE: {
        return rateTable.productModifier?.physiciansPercentage || 0;
      }
      default: {
        return 0;
      }
    }
  }

  @UserNotify({
    successMessage: marker("Aggiornato"),
    errorMessage: marker("Impossibile aggiornare")
  })
  updateItem(
    bookId: string,
    bookItemId: string,
    request: Partial<BookItem>
  ): Observable<Book> {
    return this.http.patch<Book>(
      `${environment.snoots.api.billing}/books/${bookId}/items/${bookItemId}`,
      request
    );
  }

  @UserNotify({
    successMessage: () => marker(`Eseguito da aggiornato`),
    errorMessage: marker("Impossibile aggiornare")
  })
  setExecutors({
    bookId,
    bookItemId,
    executors
  }: {
    bookId: string;
    bookItemId: string;
    executors: Array<{ id: string; name: string; percentage: number }>;
  }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.billing}/books/${bookId}/items/${bookItemId}/set-executors`,
      {
        executedBy: executors
      }
    );
  }

  @UserNotify({
    successMessage: () => marker(`Data di esecuzione aggiornata`),
    errorMessage: marker("Impossibile aggiornare")
  })
  setExecutionDate({
    bookId,
    bookItemId,
    executedAt
  }: {
    bookId: string;
    bookItemId: string;
    executedAt: Date;
  }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.billing}/books/${bookId}/items/${bookItemId}/set-execution-date`,
      {
        executedAt: executedAt
      }
    );
  }

  @UserNotify({
    successMessage: marker("Item rimosso"),
    errorMessage: marker("Impossibile rimuovere l'item selezionato")
  })
  remove(id: string, bookItemId: string): Observable<Book> {
    return this.http.delete<Book>(
      `${environment.snoots.api.billing}/books/${id}/items/${bookItemId}`
    );
  }

  @UserNotify({
    successMessage: marker("Creato conto"),
    errorMessage: marker("Impossibile creare il conto")
  })
  create(patientId: string): Observable<Book> {
    return this.http.post<Book>(
      `${environment.snoots.api.billing}/patients/${patientId}/books`,
      {}
    );
  }

  countByPatient(id: string): Observable<number> {
    return this.load(id).pipe(
      map(
        (list) =>
          first(list)?.items?.filter(
            (item) => !item.invoice || !item.invoice.emittedBy
          ).length || 0
      )
    );
  }

  async print(
    items: Array<{
      date: string;
      items: BookItem[] | undefined;
      total?: number;
    }>,
    patient: PetLight,
    origin?: string,
    invoiced?: string,
    rangeFrom?: Date,
    rangeTo?: Date
  ): Promise<void> {
    try {
      await this.printService.printPdf(
        this.toPdf(items, patient, origin, invoiced, rangeFrom, rangeTo)
      );
    } catch (ex) {
      console.error(ex);
      throw ex;
    }
  }

  async toPdf(
    items: Array<{
      date: string;
      items: BookItem[] | undefined;
      total?: number;
    }>,
    patient: PetLight,
    origin?: string,
    invoiced?: string,
    rangeFrom?: Date,
    rangeTo?: Date
  ): Promise<Blob> {
    const [
      invoiceTemplate,
      tailwindCss,
      invoiceHeaderTemplate,
      invoiceFooterTemplate
    ] = await Promise.all([
      fetch("/assets/ejs-templates/book/book.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/book/tailwind.css").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/header.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/book/book-footer.ejs").then((response) =>
        response.text()
      )
    ]);

    const [defaultLetterhead] = await Promise.all([
      lastValueFrom(this.letterheadBackend.loadDefaultLight())
    ]);

    const viewModel = bookToBookPrintViewModel({
      items,
      patient,
      origin,
      invoiced,
      rangeFrom,
      rangeTo,
      moduleService: this.moduleService,
      regionService: this.regionService
    });

    const { logoData, secondaryImageData } = await lastValueFrom(
      forkJoin({
        logoData: iif(
          () => !!defaultLetterhead?.header?.logo?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.logo!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        ),
        secondaryImageData: iif(
          () => !!defaultLetterhead?.header?.secondaryImage?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.secondaryImage!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        )
      })
    );

    const htmlHeadeRendered = this.templateService.render(
      invoiceHeaderTemplate,
      {
        ...viewModel,
        styles: "",
        content: defaultLetterhead?.header?.html || "",
        title: "",
        logo: logoData,
        secondaryImage: secondaryImageData,
        logoSize: defaultLetterhead?.header?.logo?.size || 1,
        secondaryImageSize:
          defaultLetterhead?.header?.secondaryImage?.size || 1,
        tailwindCss
      }
    );

    const htmlFooterRendered = this.templateService.render(
      invoiceFooterTemplate,
      {
        ...viewModel,
        tailwindCss
      }
    );

    const htmlRendered = this.templateService.render(invoiceTemplate, {
      ...viewModel,
      tailwindCss
    });

    return await this.printService.htmlToPdf({
      header: htmlHeadeRendered,
      footer: htmlFooterRendered,
      content: htmlRendered
    });
  }
}

function sortBookItems(book: Book): Book {
  return {
    ...book,
    items: book.items?.sort((leftItem, rightItem) => {
      return (
        dateUtil(rightItem.executedAt).toDate().getTime() -
        dateUtil(leftItem.executedAt).toDate().getTime()
      );
    })
  };
}

function extendBook(book: Book): Book {
  return {
    ...book,
    items: book.items
      ? book.items.map((bookItem: BookItem) => {
          return {
            ...bookItem,
            invoice: bookItem.invoice
              ? {
                  ...bookItem.invoice,
                  draft: !bookItem.invoice.emittedBy
                }
              : bookItem.invoice
          };
        })
      : book.items
  };
}

function bookToBookPrintViewModel({
  items,
  patient,
  origin,
  invoiced,
  rangeFrom,
  rangeTo,
  regionService,
  moduleService
}: {
  items: Array<{
    date: string;
    items: BookItem[] | undefined;
    total?: number;
  }>;
  patient: PetLight;
  origin: string | undefined;
  invoiced: string | undefined;
  rangeFrom: Date | undefined;
  rangeTo: Date | undefined;
  regionService: RegionService;
  moduleService: ModuleService;
}): any {
  const isCustomerAddressEnabled = moduleService.canBeActivatedSync(
    MODULES.INVOICE_CUSTOMER_ADDRESS
  );

  let filter: string = "";
  if (rangeFrom || rangeTo) {
    filter = sntTranslate(marker("Vista del conto filtrata da from al to"), {
      from: dateUtil(rangeFrom).format("DD/MM/YYYY"),
      to: dateUtil(rangeTo)?.format("DD/MM/YYYY")
    });
  }
  if (origin && filter !== "") {
    filter += sntTranslate(marker("e dal filtro per origin"), {
      origin: sntTranslate(origin)
    });
  } else if (origin) {
    filter = sntTranslate(marker("Vista del conto filtrata per origin"), {
      origin: sntTranslate(origin)
    });
  }
  if (invoiced && filter !== "") {
    filter += sntTranslate(marker("e dal filtro per invoiced"), {
      invoiced: sntTranslate(invoiced)
    });
  } else if (invoiced) {
    filter = sntTranslate(marker("Vista del conto filtrata per invoiced"), {
      invoiced: sntTranslate(invoiced)
    });
  }

  const medicalServicesRateable = sum(
    items
      .map((item) =>
        item.items
          ?.filter((x) => x.type === "medical-service")
          .map((x) => x.adjustedTotal)
      )
      .flat()
  );
  const productsRateable = sum(
    items
      .map((item) =>
        item.items
          ?.filter((x) => x.type === "product-sale")
          .map((x) => x.adjustedTotal)
      )
      .flat()
  );
  const drugsAssignmentRateable = sum(
    items
      .map((item) =>
        item.items
          ?.filter((x) => x.type === "drug-assignment")
          .map((x) => x.adjustedTotal)
      )
      .flat()
  );
  const parapharmaceuticalRateable = sum(
    items
      .map((item) =>
        item.items
          ?.filter((x) => x.type === "parapharmaceutical")
          .map((x) => x.adjustedTotal)
      )
      .flat()
  );
  const total = sum(items.map((item) => item.total || 0));

  const rowsFlat = items.reduce(
    (
      acc: Array<{
        date: string;
        items: Omit<BookItem, "adjustedTotal"> & {
          executedName: string;
          adjustedTotal: string;
        };
        total?: number;
      }>,
      current
    ) => {
      current.items?.forEach((item) => {
        acc.push({
          date: current.date,
          items: {
            ...item,
            executedName: first(item.executedBy)?.name || "",
            adjustedTotal: formatCurrency({
              value: round(item.adjustedTotal, 2),
              regionService
            })
          },
          total: current.total
        });
      });
      return acc;
    },
    []
  );

  let currentDate: string = "";
  return {
    customerNominative: getCustomerNominative(patient.owner),
    customerAddressLine1: patient.owner?.address?.address || "",
    customerAddressLine2: `${patient.owner?.address?.zipCode || ""} - ${patient.owner?.address?.city || ""} (${
      patient.owner?.address?.state || ""
    })`,

    patientName: patient.name,
    patientBreed: sntTranslate(patient.breed),
    patientSpecies: sntTranslate(patient.species),
    patientMicrochip: patient.microchip,
    filter,
    drugsAssignmentAdjustedTotal:
      drugsAssignmentRateable > 0
        ? formatCurrency({
            value: round(drugsAssignmentRateable, 2),
            regionService
          })
        : "",
    parapharmaceuticalAdjustedTotal:
      parapharmaceuticalRateable > 0
        ? formatCurrency({
            value: round(parapharmaceuticalRateable, 2),
            regionService
          })
        : "",
    medicalServicesAdjustedTotal:
      medicalServicesRateable > 0
        ? formatCurrency({
            value: round(medicalServicesRateable, 2),
            regionService
          })
        : "",
    productsAdjustedTotal:
      productsRateable > 0
        ? formatCurrency({
            value: round(productsRateable, 2),
            regionService
          })
        : "",
    adjustedTotal:
      total > 0
        ? formatCurrency({
            value: round(total, 2),
            regionService
          })
        : "",
    rows: rowsFlat.map((item) => {
      let date = "";
      if (currentDate !== item.date) {
        date = item.date;
        currentDate = item.date;
      }
      return {
        date,
        items: item.items,
        total: item.total
      };
    }),
    enableCustomerAddress: isCustomerAddressEnabled,
    patientLabel: sntTranslate(marker("Paziente")),
    medicalServicesLabel: sntTranslate(marker("Prestazioni")),
    productsLabel: sntTranslate(marker("Prodotti")),
    drugsLabel: sntTranslate(marker("Farmaci")),
    parapharmaceuticalsLabel: sntTranslate(marker("Parafarmaci")),
    spettabileLabel: sntTranslate(marker("Spettabile")),
    footerName: sntTranslate(marker("Conto")),
    dateLabel: sntTranslate(marker("Data")),
    descriptionLabel: sntTranslate(marker("Descrizione")),
    staffLabel: sntTranslate(marker("Staff")),
    qtaLabel: sntTranslate(marker("Qtà")),
    totalLabel: sntTranslate(marker("Totale"))
  };
}
